import { t, Trans } from '@lingui/macro'
import { useReferrerAggregation } from '@om1/aspen-api/codegen/hooks/useReferrerAggregation'
import { motion } from 'framer-motion'
import { HelpCircle, Users2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useHistory } from 'react-router-dom'
import { AspenRoutes } from '../routes'

const SCROLL_LIST_CLASS = 'space-y-3 max-h-[160px] overflow-y-auto pr-1'

/** Fades the bottom edge when more content is below the fold (scrollbars are often hidden on macOS). */
const ScrollFadeList = ({ children, className = SCROLL_LIST_CLASS }: { children: ReactNode; className?: string }) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [showScrollHint, setShowScrollHint] = useState(false)

    const updateScrollHint = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        const hasOverflow = el.scrollHeight > el.clientHeight + 1
        const notAtBottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1
        setShowScrollHint(hasOverflow && notAtBottom)
    }, [])

    useEffect(() => {
        updateScrollHint()
        const el = scrollRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(updateScrollHint)
        observer.observe(el)
        return () => observer.disconnect()
    }, [updateScrollHint, children])

    return (
        <div className='relative'>
            <div ref={scrollRef} onScroll={updateScrollHint} className={className}>
                {children}
            </div>
            {showScrollHint ? (
                <div
                    className='pointer-events-none absolute bottom-0 left-0 right-0 h-7 bg-gradient-to-t from-white via-white/90 to-transparent'
                    aria-hidden
                />
            ) : null}
        </div>
    )
}

export interface ReferrerRow {
    referrerId: string
    label: string
    count: number
    isUnknown?: boolean
    /** True when the referrer_id was resolved against REFERRER_LABELS. */
    isKnown?: boolean
}

const ACCENT_COLOR = 'hsl(174deg 54.89% 46.99%)'
const ACCENT_BG = 'hsl(174deg 54.89% 46.99% / 0.12)'

// ---------------------------------------------------------------------------
// Known referrer_id → source-name mapping (per APP-567).
//
// TEMPORARY: this lives in the frontend so we can ship the widget labels
// without a backend migration. The longer-term plan is a dedicated
// ``aspen.referrer_label`` table queried by the aggregation endpoint, at
// which point this constant goes away and the API returns ``source_name``.
//
// When adding/removing entries here, mirror the change in any future
// referrer_label seed migration so the two stay in sync.
// ---------------------------------------------------------------------------
// Total equals metricCards.Candidates count (1284) for sanity check
const ReferrerWidget = () => {
    const referrerLabels: Record<string, string> = {
        e958419d1b01005803a185bdfd6f6733974b4c96a0e5592cbe03a6fd1874bed3: t`PINC AI`,
        '9781fafbd71d05aa1ab51e61585c6ccd5ca3aea0e1bbb72c0349f95c44ccefb7': t`IQVIA`,
        om1: t`OM1`,
        neurodiscovery: t`Neuro Discovery`
    }
    const history = useHistory()
    const { items, loading } = useReferrerAggregation()

    // Navigate to the Candidate worklist with this referrer pre-applied as a
    // filter (APP-567 follow-up). The Candidates page reads the ``referrer``
    // query param and seeds its ``referrerId`` filter from it. ``referrerName``
    // carries the human-readable label so the page can surface it in the UI.
    const goToCandidatesForReferrer = (referrerId: string, referrerName: string) => {
        history.push(`/${AspenRoutes.CANDIDATES}?referrer=${encodeURIComponent(referrerId)}&referrerName=${encodeURIComponent(referrerName)}`)
    }
    const referrerBreakdown: ReferrerRow[] = items.map((item) => {
        const isUnknown = item.referrerId === 'unknown' || item.referrerId === ''
        const friendlyName = referrerLabels[item.referrerId]
        const isKnown = !isUnknown && friendlyName !== undefined
        // Resolution order:
        //   1. empty / "unknown" → "No referrer" bucket (existing AC).
        //   2. known referrer_id → human-readable source name.
        //   3. unknown but non-empty → raw referrer_id (Jay's spec: still
        //      surface unrecognised ids so we don't hide data).
        let label: string
        if (isUnknown) {
            label = t`No referrer`
        } else if (isKnown) {
            label = friendlyName
        } else {
            label = item.referrerId
        }
        return {
            referrerId: item.referrerId,
            label,
            count: item.count,
            isUnknown,
            isKnown
        }
    })
    const sorted = [...referrerBreakdown].sort((a, b) => b.count - a.count)
    const total = sorted.reduce((sum, r) => sum + r.count, 0)
    const max = Math.max(...sorted.map((r) => r.count), 1)
    const isLoading = loading
    const isEmpty = !loading && sorted.length === 0

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            className='bg-white rounded-lg p-5 card-shadow border border-border'
        >
            <div className='flex items-center justify-between mb-5'>
                <div>
                    <p className='text-base font-semibold text-gray-700'>
                        <Trans>Candidates by Referrer</Trans>
                    </p>
                    <p className='text-xs text-muted-foreground mt-0.5'>
                        {t`${total.toLocaleString()} candidates across ${sorted.length} referrers`}
                    </p>
                </div>
                <div className='p-2 rounded-lg' style={{ backgroundColor: ACCENT_BG }}>
                    <Users2 size={16} style={{ color: ACCENT_COLOR }} />
                </div>
            </div>

            {isLoading ? (
                <ScrollFadeList>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className='h-8 rounded bg-muted animate-pulse' />
                    ))}
                </ScrollFadeList>
            ) : isEmpty ? (
                <div className='flex flex-col items-center justify-center py-6 text-center'>
                    <HelpCircle size={32} className='text-muted-foreground mb-2' />
                    <p className='text-sm text-muted-foreground'>
                        <Trans>No candidates yet</Trans>
                    </p>
                    <p className='text-xs text-muted-foreground mt-1'>
                        <Trans>Referrer attribution will appear here once candidates are added.</Trans>
                    </p>
                </div>
            ) : (
                <ScrollFadeList>
                    {sorted.map((row) => {
                        const pct = total > 0 ? (row.count / total) * 100 : 0
                        const barWidth = (row.count / max) * 100
                        return (
                            <button
                                key={row.referrerId}
                                type='button'
                                onClick={() => goToCandidatesForReferrer(row.referrerId, row.label)}
                                aria-label={t`View candidates referred by ${row.label}`}
                                className='w-full space-y-1.5 text-left rounded-md p-1.5 -m-1.5 cursor-pointer transition-colors hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-primary'
                            >
                                <div className='flex items-center justify-between text-xs'>
                                    <div className='flex items-center gap-2 min-w-0'>
                                        <span
                                            className={`font-medium truncate ${
                                                row.isUnknown
                                                    ? 'text-muted-foreground italic'
                                                    : row.isKnown
                                                    ? 'text-card-foreground'
                                                    : 'text-card-foreground font-mono'
                                            }`}
                                        >
                                            {row.label}
                                        </span>
                                    </div>
                                    <div className='flex items-center gap-2 shrink-0'>
                                        <span className='font-semibold text-card-foreground tabular-nums'>{row.count.toLocaleString()}</span>
                                        <span className='text-muted-foreground tabular-nums w-10 text-right'>{pct.toFixed(1)}%</span>
                                    </div>
                                </div>
                                <div className='h-2 rounded-full bg-muted overflow-hidden'>
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${barWidth}%` }}
                                        transition={{ duration: 0.6, ease: 'easeOut' }}
                                        className={`h-full rounded-full ${row.isUnknown ? 'bg-muted-foreground/40' : ''}`}
                                        style={row.isUnknown ? undefined : { backgroundColor: ACCENT_COLOR }}
                                    />
                                </div>
                            </button>
                        )
                    })}
                </ScrollFadeList>
            )}
        </motion.div>
    )
}

export default ReferrerWidget
